import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { ClipboardCheck, FileSignature, UserPlus, Send, Radio, CheckCircle2, Ban, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { navFor, isActiveNav, openNav } from "@/lib/navigation";
import { BrandLogo } from "@/components/brand/AppHeader";
import { STAGES } from "@/lib/pipelineApi";
import { CompanySwitcher } from "@/components/CompanySwitcher";

// Every menu reads lib/navigation.js; `prefix` keeps sidebar (sidebar-*) and mobile (menu-*) test ids distinct.
export function NavList({ onGo, logout: withLogout, prefix = "sidebar" }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  return (
    <nav className="space-y-0.5">
      <CompanySwitcher />
      {navFor(user).map((n) => {
        const { label, icon: Icon, id, soon, hint } = n;
        const on = isActiveNav(n, pathname);
        return (
          <button key={id} type="button" data-testid={`${prefix}-${id}`} disabled={soon} title={soon ? "Coming soon" : hint || label} style={soon ? { opacity: 1 } : undefined}
            aria-current={on ? "page" : undefined} onClick={() => { onGo?.(); openNav(n, navigate); }}
            className={`w-full flex items-center gap-3 h-10 px-3 rounded-lg text-sm text-left transition-colors ${on ? "bg-white/[0.12] text-white font-semibold" : soon ? "text-white/55 cursor-not-allowed" : "text-white/80 hover:bg-white/[0.08] hover:text-white"}`}>
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{label}</span>
            {soon && <span className="text-[9px] uppercase tracking-wider text-white/60 border border-white/20 rounded-full px-1.5 py-0.5">Soon</span>}
          </button>
        );
      })}
      {withLogout && (
        <button type="button" data-testid="menu-logout" onClick={logout}
          className="w-full flex items-center gap-3 h-10 px-3 rounded-lg text-sm text-white/80 hover:bg-white/[0.08] hover:text-white"><LogOut className="h-4 w-4" /> Log out</button>
      )}
    </nav>
  );
}

// The one mobile menu: header stays put, the list scrolls, and the last item clears the phone's browser bar.
export function MobileNavSheet({ open, onOpenChange, footer }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="mobile-menu" className="on-dark w-[82vw] max-w-xs p-0 border-white/10 flex flex-col h-[100dvh] [&>button]:text-white">
        <div className="shrink-0 px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-3 border-b border-white/10">
          <BrandLogo className="h-9 w-auto" /><SheetTitle className="sr-only">Menu</SheetTitle><SheetDescription className="sr-only">All features</SheetDescription>
        </div>
        <div data-testid="mobile-menu-scroll" className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <NavList prefix="menu" onGo={() => onOpenChange(false)} logout />
          {footer && <div className="mt-4 pt-4 border-t border-white/10">{footer}</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function MobileMenuBar() {
  const [open, setOpen] = useState(false);
  return (
    <header data-testid="mobile-menu-bar" className="on-dark lg:hidden sticky top-0 z-30 flex items-center gap-2 px-4 h-14">
      <BrandLogo className="h-8 w-auto" /><span className="ml-auto" />
      <button type="button" data-testid="mobile-menu-button" onClick={() => setOpen(true)} aria-label="Open menu" className="h-10 w-10 inline-flex items-center justify-center rounded-full text-white hover:bg-white/10"><Menu className="h-5 w-5" /></button>
      <MobileNavSheet open={open} onOpenChange={setOpen} />
    </header>
  );
}

export function Sidebar() {
  const { logout } = useAuth();
  return (
    <aside data-testid="pipeline-sidebar" className="on-dark hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col px-3 py-5 z-30 overflow-y-auto">
      <div className="px-2 mb-7"><BrandLogo className="h-11 w-auto" /></div>
      <NavList />
      <button type="button" data-testid="sidebar-logout" onClick={logout}
        className="mt-auto flex items-center gap-3 h-10 px-3 rounded-lg text-sm text-white/80 hover:bg-white/[0.08] hover:text-white">
        <LogOut className="h-4 w-4" /> Log out
      </button>
    </aside>
  );
}

const STAGE_ICON = { leads: UserPlus, approvals: ClipboardCheck, contracts_ads: FileSignature, ready: Send, ongoing: Radio, completed: CheckCircle2, withdrawn: Ban };
const SHORT = { leads: "Leads", approvals: "Approvals", contracts_ads: "Contracts", ready: "Ready", ongoing: "Ongoing", completed: "Done", withdrawn: "Withdrawn" };
export { STAGE_ICON };

export function StageBottomNav({ tab, setTab, count }) {
  return (
    <nav data-testid="pipeline-stage-nav" className="on-dark lg:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-6 border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
      {STAGES.map(([k, l]) => {
        const Icon = STAGE_ICON[k];
        const on = tab === k;
        return (
          <button key={k} type="button" data-testid={`pipeline-tab-${k}`} aria-selected={on} aria-label={l} onClick={() => { setTab(k); window.scrollTo({ top: 0 }); }}
            className={`relative flex flex-col items-center justify-center gap-0.5 min-h-[60px] text-[10px] font-semibold transition-colors ${on ? "text-white" : "text-white/60"}`}>
            {on && <span aria-hidden className="absolute top-0 h-0.5 w-8 rounded-full bg-snow" />}
            <span className="relative">
              <Icon className="h-5 w-5" strokeWidth={on ? 2.3 : 1.8} />
              <span data-testid={`pipeline-tab-count-${k}`} className={`absolute -top-1.5 -right-3 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] leading-[18px] text-center ${on ? "bg-snow text-ink" : "bg-white/15 text-white"}`}>{count(k)}</span>
            </span>
            {SHORT[k]}
          </button>
        );
      })}
    </nav>
  );
}
